import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Heart, IndianRupee, Info } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { usePageTitle } from '../hooks/usePageTitle';
import { useSiteSettings } from '../hooks/useSiteSettings';
import { useT } from '../contexts/LanguageContext';
import { loadRazorpay } from '../lib/loadRazorpay';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PRESET_AMOUNTS = [500, 1000, 2500, 5000, 10000];

export const Donate = () => {
  usePageTitle('Donate');
  const settings = useSiteSettings();
  const tr = useT();
  const [form, setForm] = useState({
    amount: 1000,
    donor_name: '',
    donor_email: '',
    donor_phone: '',
    pan_number: '',
    address: '',
    wants_80g_receipt: false,
  });
  const [processing, setProcessing] = useState(false);
  const navigate = useNavigate();

  const handleDonate = async (e) => {
    e.preventDefault();
    if (form.amount < 10) {
      toast.error('Minimum donation is ₹10');
      return;
    }
    if (form.wants_80g_receipt && !form.pan_number) {
      toast.error('PAN number is required for 80(G) tax exemption receipt');
      return;
    }

    setProcessing(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/donations/create-order`, {
        amount: form.amount * 100,
        donor_name: form.donor_name,
        donor_email: form.donor_email,
        donor_phone: form.donor_phone,
        pan_number: form.pan_number,
        address: form.address,
        wants_80g_receipt: form.wants_80g_receipt,
      });

      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount: data.amount,
        currency: data.currency,
        order_id: data.order_id,
        name: 'Rishidev Foundation',
        description: 'Donation for Spiritual Preservation & Teachings',
        handler: async (response) => {
          try {
            const { data: verifyData } = await axios.post(`${API_URL}/api/donations/verify`, response);
            sessionStorage.setItem('rf_last_donation', JSON.stringify({
              receipt_url: `${API_URL}${verifyData.receipt_url}`,
              amount: form.amount,
              name: form.donor_name,
              order_id: verifyData.order_id,
            }));
            navigate('thank-you');
          } catch (error) {
            toast.error('Verification failed. Please contact us.');
          } finally {
            setProcessing(false);
          }
        },
        modal: {
          ondismiss: () => setProcessing(false),
        },
        prefill: {
          name: form.donor_name,
          email: form.donor_email,
          contact: form.donor_phone,
        },
        theme: { color: '#E67E22' },
      };

      if (!(await loadRazorpay())) {
        toast.error('Could not load the payment gateway. Please check your connection or disable ad-blockers and try again.');
        setProcessing(false);
        return;
      }
      const razorpay = new window.Razorpay(options);
      razorpay.open();
    } catch (error) {
      console.error('Donation init failed', error);
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to initiate donation');
      setProcessing(false);
    }
  };

  const taxNumber = settings.tax_exemption_number;

  return (
    <div className="min-h-screen pt-20 bg-cream" data-testid="donate-page">
      <section className="py-24 md:py-32">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <div className="w-20 h-20 mx-auto bg-gradient-to-br from-saffron-500 to-gold-500 rounded-full flex items-center justify-center mb-6 shadow-lg">
              <Heart className="text-white" size={36} fill="white" />
            </div>
            <div className="text-saffron-600 uppercase text-xs md:text-sm tracking-[0.2em] font-body font-semibold mb-4">
              {tr('Support Our Mission')}
            </div>
            <h1 className="text-4xl md:text-6xl font-heading font-bold text-charcoal-800 tracking-tighter leading-none mb-6">
              {tr('Make a Donation')}
            </h1>
            <p className="text-lg md:text-xl text-charcoal-600 font-body leading-relaxed max-w-3xl mx-auto">
              {tr('Your generosity sustains our sacred work of preserving Sanatana Dharma teachings and offering them freely to seekers worldwide.')}
            </p>
            {taxNumber && (
              <div className="mt-6 inline-flex items-center gap-2 bg-saffron-50 text-saffron-700 px-4 py-2 rounded-full text-sm font-medium">
                <Info size={16} /> {tr('Eligible for 80(G) tax exemption')}
              </div>
            )}
          </motion.div>

          <motion.form
            onSubmit={handleDonate}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-white rounded-xl shadow-lg border border-charcoal-100 p-8 md:p-10 space-y-6"
            data-testid="donation-form"
          >
            <div>
              <label className="block text-sm font-body font-medium text-charcoal-700 mb-3">
                {tr('Choose Amount (₹) *')}
              </label>
              <div className="grid grid-cols-3 md:grid-cols-5 gap-2 mb-3">
                {PRESET_AMOUNTS.map((amt) => (
                  <button
                    key={amt}
                    type="button"
                    onClick={() => setForm({ ...form, amount: amt })}
                    data-testid={`amount-${amt}`}
                    className={`px-4 py-3 rounded-lg font-body font-medium transition-colors ${
                      form.amount === amt
                        ? 'bg-saffron-600 text-white'
                        : 'bg-cream border border-charcoal-200 text-charcoal-700 hover:bg-saffron-50'
                    }`}
                  >
                    ₹{amt.toLocaleString()}
                  </button>
                ))}
              </div>
              <div className="relative">
                <IndianRupee size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-charcoal-500" />
                <input
                  type="number"
                  min="10"
                  required
                  value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: parseInt(e.target.value) || 0 })}
                  placeholder={tr('Custom amount')}
                  data-testid="donation-amount-input"
                  className="w-full pl-10 pr-4 py-3 rounded-lg border border-charcoal-200 focus:outline-none focus:ring-2 focus:ring-saffron-500"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <input type="text" placeholder={tr('Full Name *')} required value={form.donor_name} onChange={(e) => setForm({ ...form, donor_name: e.target.value })} data-testid="donor-name" className="w-full px-4 py-3 rounded-lg border border-charcoal-200 focus:outline-none focus:ring-2 focus:ring-saffron-500" />
              <input type="email" placeholder={tr('Email *')} required value={form.donor_email} onChange={(e) => setForm({ ...form, donor_email: e.target.value })} data-testid="donor-email" className="w-full px-4 py-3 rounded-lg border border-charcoal-200 focus:outline-none focus:ring-2 focus:ring-saffron-500" />
            </div>
            <input type="tel" placeholder={tr('Phone (optional)')} value={form.donor_phone} onChange={(e) => setForm({ ...form, donor_phone: e.target.value })} data-testid="donor-phone" className="w-full px-4 py-3 rounded-lg border border-charcoal-200 focus:outline-none focus:ring-2 focus:ring-saffron-500" />

            <div className="bg-saffron-50 border border-saffron-200 rounded-lg p-4 space-y-3">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.wants_80g_receipt}
                  onChange={(e) => setForm({ ...form, wants_80g_receipt: e.target.checked })}
                  data-testid="wants-80g-checkbox"
                  className="mt-1"
                />
                <div>
                  <div className="font-medium text-charcoal-800">{tr('Request 80(G) Tax Exemption Receipt')}</div>
                  <div className="text-sm text-charcoal-600 mt-1">
                    {tr('Get a receipt eligible for tax deduction under Section 80(G) of the Income Tax Act, 1961.')}
                    {taxNumber && <span className="block text-xs mt-1">{tr('Ref')}: {taxNumber}</span>}
                  </div>
                </div>
              </label>
              {form.wants_80g_receipt && (
                <div className="space-y-3 pl-8">
                  <input type="text" placeholder={tr('PAN Number * (required for 80G)')} required value={form.pan_number} onChange={(e) => setForm({ ...form, pan_number: e.target.value.toUpperCase() })} data-testid="pan-input" className="w-full px-4 py-3 rounded-lg border border-charcoal-200 focus:outline-none focus:ring-2 focus:ring-saffron-500 uppercase" maxLength={10} />
                  <textarea placeholder={tr('Address (for receipt)')} rows={2} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="donor-address" className="w-full px-4 py-3 rounded-lg border border-charcoal-200 focus:outline-none focus:ring-2 focus:ring-saffron-500" />
                </div>
              )}
            </div>

            <button
              type="submit"
              disabled={processing}
              data-testid="donate-submit-button"
              className="w-full bg-gradient-to-r from-saffron-600 to-gold-500 text-white rounded-full px-8 py-4 font-body font-medium text-lg hover:opacity-95 transition-opacity shadow-lg disabled:opacity-50 inline-flex items-center justify-center gap-2"
            >
              <Heart size={20} fill="white" /> {processing ? tr('Processing...') : `${tr('Donate')} ₹${(form.amount || 0).toLocaleString()}`}
            </button>

            <p className="text-xs text-center text-charcoal-500">
              {tr('Secure payment via Razorpay. UPI, Cards, Net Banking accepted. Receipt sent to your email.')}
            </p>
          </motion.form>
        </div>
      </section>
    </div>
  );
};
